import { type FC, useState, useEffect } from "react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  UploadItem,
  UploadItemStatus,
  UploadItemType,
} from "./types/upload-item";
import { buildHierarchy } from "./utils/hierachy";
import FileUploadItem from "./FileUploadItem";
import FolderUploadItem from "./FolderUploadItem";

type Props = {
  uploadItems?: UploadItem[];
  onClose?: () => void;
};

const UploadFilesPopoverList: FC<Props> = ({ uploadItems, onClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState(true);

  // Update open state when uploads change - show if there are active uploads
  useEffect(() => {
    const hasActiveUploads =
      uploadItems && uploadItems.length > 0
        ? uploadItems.some(
            (u) =>
              u.status !== UploadItemStatus.Completed &&
              u.status !== UploadItemStatus.Failed
          )
        : false;
    if (hasActiveUploads) {
      setOpen(true);
    }
  }, [uploadItems]);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const close = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  if (!open || !uploadItems || uploadItems.length === 0) {
    return null;
  }

  const hierarchicalItems = buildHierarchy(uploadItems);

  return (
    <div
      className={cn(
        "fixed bottom-0 right-0 sm:bottom-4 sm:right-4",
        "w-full sm:max-w-[min(90vw,24rem)] md:max-w-[min(85vw,26rem)] lg:max-w-[min(80vw,28rem)]",
        "border rounded-t-lg sm:rounded-lg bg-popover shadow-xl",
        "transition-opacity duration-300",
        "max-h-[70vh] sm:max-h-[80vh] flex flex-col overflow-hidden",
        "z-50",
        open ? "opacity-100" : "opacity-0"
      )}
    >
      <div className="flex justify-between items-center gap-2 border-b p-3 shrink-0">
        <h3 className="text-sm sm:text-base font-semibold truncate min-w-0 flex-1">
          {(() => {
            const uploadingCount =
              uploadItems?.filter(
                (ui) =>
                  ui.status !== UploadItemStatus.Completed &&
                  ui.status !== UploadItemStatus.Failed
              ).length || 0;
            const successCount =
              uploadItems?.filter(
                (ui) => ui.status === UploadItemStatus.Completed
              ).length || 0;
            const errorCount =
              uploadItems?.filter((ui) => ui.status === UploadItemStatus.Failed)
                .length || 0;

            switch (true) {
              case uploadingCount > 0:
                return "Uploader...";
              case uploadingCount === 0 && successCount > 0 && errorCount > 0:
                return `${successCount} uploadet, ${errorCount} fejl`;
              case uploadingCount === 0 && successCount > 0 && errorCount === 0:
                return `${successCount} uploadet`;
              case uploadingCount === 0 && successCount === 0 && errorCount > 0:
                return `${errorCount} fejl`;
              default:
                return `${uploadItems?.length} items`;
            }
          })()}
        </h3>
        <div className="flex gap-1 shrink-0">
          <Button
            onClick={toggleCollapse}
            variant="ghost"
            size="icon"
            className="size-8"
          >
            {collapsed ? (
              <ChevronUp className="size-4" aria-hidden="true" />
            ) : (
              <ChevronDown className="size-4" aria-hidden="true" />
            )}
          </Button>
          <Button
            onClick={close}
            variant="ghost"
            size="icon"
            className="size-8"
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div
        className={cn(
          "transition-all duration-300 flex-1 min-h-0",
          collapsed
            ? "max-h-0 opacity-0 overflow-hidden"
            : "opacity-100 overflow-y-auto overscroll-contain"
        )}
      >
        <ul className="p-2 sm:p-3 space-y-1.5">
          {hierarchicalItems.map((uploadItem) => {
            if (uploadItem.type === UploadItemType.FOLDER) {
              return (
                <FolderUploadItem key={uploadItem.id} folder={uploadItem} />
              );
            }
            return (
              <FileUploadItem key={uploadItem.id} uploadItem={uploadItem} />
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default UploadFilesPopoverList;
